import FormQuestionType from '@opsbase/lib-form/FormQuestionType';
import Palette from '@opsbase/lib-palette';
import ImageViewer from '@opsbase/web-image/ImageViewer';
import range from 'lodash/range';
import React, { useContext, useMemo } from 'react';
import { HashLink } from 'react-router-hash-link';
import useReportCSS from '../../useReportCSS';
import DetailsReportSwab from '../DetailsReportSwab';
import DetailsOpReportAnswerCell from './DetailsOpReportAnswerCell';
import DetailsOpReportAnswerCheck from './DetailsOpReportAnswerCheck';
import DetailsOpReportAnswerDateTime from './DetailsOpReportAnswerDateTime';
import DetailsOpReportAnswerList from './DetailsOpReportAnswerList';
import DetailsOpReportAnswerNumber from './DetailsOpReportAnswerNumber';
import DetailsOpReportAnswerRating from './DetailsOpReportAnswerRating';
import DetailsOpReportAnswerSPT from './DetailsOpReportAnswerSPT';
import DetailsOpReportAnswerText from './DetailsOpReportAnswerText';
import DetailsOpReportContext from './DetailsOpReportContext';
export const DetailsOpReportQuestionContext = React.createContext(null);
export function DetailsOpReportQuestion({ questionNodeId, isRestricted }) {
    const { form } = useContext(DetailsOpReportContext);
    const q = form.questions[questionNodeId];
    const a = useMemo(() => (isRestricted ? {} : form.answers[questionNodeId]), [form.answers, isRestricted, questionNodeId]);
    const r = form.rules[q.questionId];
    const ctx = useMemo(() => ({ q, r, a }), [q, r, a]);
    return (React.createElement(DetailsOpReportQuestionContext.Provider, { value: ctx },
        q.type === FormQuestionType.INFO && React.createElement(DetailsOpReportInfoQuestion, null),
        q.type !== FormQuestionType.INFO && React.createElement(DetailsOpReportOtherQuestion, null)));
}
export default DetailsOpReportQuestion;
//---------------
// INFO QUESTION
//---------------
function DetailsOpReportInfoQuestion() {
    const { def, issues, notes } = useContext(DetailsOpReportContext);
    const { q } = useContext(DetailsOpReportQuestionContext);
    const myCss = useReportCSS(def.pdf);
    let cols = 1;
    !!(issues === null || issues === void 0 ? void 0 : issues.length) && cols++;
    !!(notes === null || notes === void 0 ? void 0 : notes.length) && cols++;
    const indent = 0;
    const noRow = false;
    const content = (React.createElement(React.Fragment, null,
        range(indent).map(i => (React.createElement("td", { key: i, className: myCss.data + ' ' + myCss.leftright }, "\u00A0"))),
        React.createElement("td", { className: myCss.info + ' ' + myCss.leftright, colSpan: 10 + cols - indent, style: { paddingLeft: noRow ? 16 : undefined } },
            q.title,
            React.createElement("br", null))));
    return noRow ? content : React.createElement("tr", { className: myCss.row }, content);
}
//---------------
// OTHER QUESTION
//---------------
function DetailsOpReportOtherQuestion() {
    const { def, issues, notes } = useContext(DetailsOpReportContext);
    const { q, r, a } = useContext(DetailsOpReportQuestionContext);
    const myCss = useReportCSS(def.pdf);
    const indent = 0;
    const noRow = false;
    let issue = issues.findIndex(i => i.id === (a === null || a === void 0 ? void 0 : a.issueId));
    if (issue > -1) {
        issue++;
    }
    else {
        issue = null;
    }
    let note = notes.findIndex(i => i.id === (a === null || a === void 0 ? void 0 : a.conversationId));
    if (note > -1) {
        note++;
    }
    else {
        note = null;
    }
    let answerCell;
    // const score = getScore(form, q, false)
    if (a.notApplicable) {
        answerCell = (React.createElement(DetailsOpReportAnswerCell, null,
            React.createElement(DetailsReportSwab, { color: Palette.GoodOldBlue }),
            'N/A'));
    }
    else {
        switch (q.type) {
            case FormQuestionType.CHECK:
                answerCell = React.createElement(DetailsOpReportAnswerCheck, null);
                break;
            case FormQuestionType.NUMBER:
                answerCell = React.createElement(DetailsOpReportAnswerNumber, null);
                break;
            case FormQuestionType.LIST:
                answerCell = React.createElement(DetailsOpReportAnswerList, null);
                break;
            case FormQuestionType.PHOTO:
            case FormQuestionType.SIGNATURE:
                if (a.photos) {
                    const photosArr = a.photos.split(',') || [];
                    answerCell = (React.createElement("td", { className: myCss.data },
                        React.createElement(ImageViewer, { fileNames: photosArr })));
                }
                break;
            case FormQuestionType.DATETIME:
                answerCell = React.createElement(DetailsOpReportAnswerDateTime, null);
                break;
            case FormQuestionType.RATING:
                answerCell = React.createElement(DetailsOpReportAnswerRating, null);
                break;
            case FormQuestionType.ISSUE:
            case FormQuestionType.TEXT:
                answerCell = React.createElement(DetailsOpReportAnswerText, null);
                break;
            case FormQuestionType.SPT:
            case FormQuestionType.ASSET:
                answerCell = React.createElement(DetailsOpReportAnswerSPT, null);
                break;
            case FormQuestionType.REPETITION:
            case FormQuestionType.SECTION:
                answerCell = React.createElement(DetailsOpReportAnswerCell, null);
                break;
            case FormQuestionType.QUESTIONSET:
                return null;
            default:
                answerCell = null;
                break;
        }
    }
    // const subQuestions = q.getSubQuestions(idx)
    const title = calcTitle(q, r);
    const content = (React.createElement(React.Fragment, null,
        range(indent).map(i => (React.createElement("td", { key: i, className: myCss.data + ' ' + myCss.leftright }, "\u00A0"))),
        React.createElement("td", { className: myCss.data + ' ' + myCss.left, colSpan: 10 - indent, style: {
                paddingLeft: noRow ? 16 : undefined,
                verticalAlign: 'top',
            }, id: 'Question_' + q.questionId },
            title,
            q.type === FormQuestionType.SECTION && q.parentNodeId === 'root' ? (React.createElement(HashLink, { style: { fontSize: 'xx-small', textTransform: 'none' }, smooth: true, to: '#section_' + q.questionId + '_home' }, ' Back')) : ('')),
        answerCell,
        !!(issues === null || issues === void 0 ? void 0 : issues.length) && (React.createElement("td", { id: 'issue' + issue + '_home', className: myCss.data + ' ' + myCss.right },
            React.createElement(HashLink, { smooth: true, to: '#Issue_' + issue }, !!issue ? 'Issue #' + issue : ''))),
        !!(notes === null || notes === void 0 ? void 0 : notes.length) && (React.createElement("td", { id: 'note' + note + '_home', className: myCss.data + ' ' + myCss.right },
            React.createElement(HashLink, { smooth: true, to: '#Note_' + note }, !!note ? 'Note #' + note : '')))));
    return noRow ? content : React.createElement("tr", { className: myCss.row }, content);
}
function calcTitle(q, r) {
    var _a, _b, _c, _d, _e, _f;
    if (q.type !== FormQuestionType.REPETITION)
        return q.title;
    const rr = r;
    let subTitle, rIdx = q.repetitionIndex;
    if ((_a = rr.nTimes) === null || _a === void 0 ? void 0 : _a.enabled) {
        subTitle = `${rIdx + 1}`;
    }
    else if ((_b = rr.unLimitedTimes) === null || _b === void 0 ? void 0 : _b.enabled) {
        subTitle = `${rIdx + 1}`;
    }
    else if ((_c = rr.list) === null || _c === void 0 ? void 0 : _c.enabled) {
        subTitle = `${(_f = (_e = (_d = rr.list) === null || _d === void 0 ? void 0 : _d.options) === null || _e === void 0 ? void 0 : _e[rIdx]) === null || _f === void 0 ? void 0 : _f.label}`;
    }
    return '(' + subTitle + ') ' + q.title;
}
